// W65 — Method 2 (B1-B5)
// =================================
// Per-ball Method-2 workbench. For each ball position, renders a 12-column table:
//   • A: Ball N (number 1..50)
//   • B: Times till 2025 — pre-2026 count at this ball position
//   • C: {draws_pre_2026} — rounded %
//   • D: R — precise % (2 decimals) over pre-2026
//   • E: 2026 — count in 2026 at this ball
//   • F: {cy_draws_count} — rounded %
//   • G: % — rounded diff (2026 % − pre-2026 %)
//   • H: R1 — diff rounded to integer
//   • I: {Month} Till 2025 — pre-2026 count of this number in current month
//   • J: {total_pre_cy_month_draws} — rounded %
//   • K: % — rounded May-vs-overall diff (pre-2026)
//   • L: R2 — diff rounded to integer
//
// "R" terminology: previously called "Parameter" (P). R is the precise % anchor;
// R1/R2 are the integer-form differences for downstream scoring.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, CaretUp, CaretDown, DownloadSimple,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const BALL_LABELS = {
  ball_1: 'Ball 1',
  ball_2: 'Ball 2',
  ball_3: 'Ball 3',
  ball_4: 'Ball 4',
  ball_5: 'Ball 5',
};

// % delta colour band (positive = emerald, negative = rose)
const deltaCls = (v) => {
  if (v == null) return 'text-slate-400';
  if (v >= 3) return 'bg-emerald-200 text-emerald-900 font-bold';
  if (v >= 1) return 'bg-emerald-100 text-emerald-800';
  if (v > -1) return 'text-slate-500';
  if (v > -3) return 'bg-rose-100 text-rose-800';
  return 'bg-rose-200 text-rose-900 font-bold';
};

// Sigma colour scale (used in main table + Master Σ tab)
const sigmaCls = (sigma) =>
    sigma >= 15 ? 'bg-emerald-400 text-emerald-950 font-black ring-2 ring-emerald-600'
  : sigma >= 8  ? 'bg-emerald-200 text-emerald-900 font-bold'
  : sigma >= 3  ? 'bg-emerald-100 text-emerald-800 font-bold'
  : sigma >= -3 ? 'text-slate-700 font-bold'
  : sigma >= -8 ? 'bg-rose-100 text-rose-800 font-bold'
  : sigma >= -15 ? 'bg-rose-200 text-rose-900 font-bold'
  : 'bg-rose-400 text-rose-950 font-black ring-2 ring-rose-600';

// Compute Σ for a single row (sum of all 7 precise per-ball diffs)
const computeRowSigma = (row) => (
    (row?.v2_diff_pct || 0)
  + (row?.v2_may_diff_pre_2026 || 0)
  + (row?.v2_cm_cy_diff || 0)
  + (row?.v2_day_diff || 0)
  + (row?.v2_last_n_diff || 0)
  + (row?.v2_same_date_diff || 0)
  + (row?.v2_cy_main_diff || 0)
);

// Synchronized top scrollbar that mirrors the wide table below it.
// Bi-directional sync so moving either bar moves the other.
const SyncedTopScrollbar = ({ tableRef, ballKey, minWidth }) => {
  const topRef = useRef(null);
  useEffect(() => {
    const top = topRef.current;
    const bottom = tableRef.current;
    if (!top || !bottom) return;
    let syncing = false;
    const onTop = () => {
      if (syncing) return;
      syncing = true;
      bottom.scrollLeft = top.scrollLeft;
      syncing = false;
    };
    const onBottom = () => {
      if (syncing) return;
      syncing = true;
      top.scrollLeft = bottom.scrollLeft;
      syncing = false;
    };
    top.addEventListener('scroll', onTop);
    bottom.addEventListener('scroll', onBottom);
    return () => {
      top.removeEventListener('scroll', onTop);
      bottom.removeEventListener('scroll', onBottom);
    };
  }, [tableRef]);
  return (
    <div
      ref={topRef}
      className="overflow-x-auto mb-1"
      style={{ height: '14px' }}
      data-testid={`w65-top-scroll-${ballKey}`}
    >
      <div style={{ width: `${minWidth}px`, height: '1px' }} />
    </div>
  );
};

// ---------- per-ball 32-column table ----------
const M2BallTable = ({
  ballKey, ballLabel, numbersData,
  drawsPre2026, cyDrawsCount, monthLabel, monthDrawsPre2026, cyCurrentMonthDraws,
  nextDrawDay, dayTotalDraws,
  lastNDraws, lastNSlots,
  sameDateDraws, sameDateSlots, nextDrawMonthLabel,
  cyMainSlots,
  sortConfig, onSort,
}) => {
  const tableScrollRef = useRef(null);
  const sorted = [...numbersData].sort((a, b) => {
    const k = sortConfig.key;
    let aVal, bVal;
    switch (k) {
      case 'number': aVal = a.number; bVal = b.number; break;
      case 'times25': aVal = a.v2_times_till_2025 || 0; bVal = b.v2_times_till_2025 || 0; break;
      case 'pct25':   aVal = a.v2_pct_pre_2026 || 0; bVal = b.v2_pct_pre_2026 || 0; break;
      case 'cy':      aVal = a.v2_cy_ball || 0; bVal = b.v2_cy_ball || 0; break;
      case 'pct26':   aVal = a.v2_pct_2026 || 0; bVal = b.v2_pct_2026 || 0; break;
      case 'diff':    aVal = a.v2_diff_pct || 0; bVal = b.v2_diff_pct || 0; break;
      case 'may':     aVal = a.v2_may_pre_2026_count || 0; bVal = b.v2_may_pre_2026_count || 0; break;
      case 'maypct':  aVal = a.v2_may_pct_pre_2026 || 0; bVal = b.v2_may_pct_pre_2026 || 0; break;
      case 'maydiff': aVal = a.v2_may_diff_pre_2026 || 0; bVal = b.v2_may_diff_pre_2026 || 0; break;
      case 'cm':      aVal = a.v2_cm_cy_count || 0; bVal = b.v2_cm_cy_count || 0; break;
      case 'cmpct':   aVal = a.v2_cm_cy_pct || 0; bVal = b.v2_cm_cy_pct || 0; break;
      case 'cmdiff':  aVal = a.v2_cm_cy_diff || 0; bVal = b.v2_cm_cy_diff || 0; break;
      case 'day':     aVal = a.v2_day_count || 0; bVal = b.v2_day_count || 0; break;
      case 'daypct':  aVal = a.v2_day_pct || 0; bVal = b.v2_day_pct || 0; break;
      case 'daydiff': aVal = a.v2_day_diff || 0; bVal = b.v2_day_diff || 0; break;
      case 'lastn':   aVal = a.v2_last_n_count || 0; bVal = b.v2_last_n_count || 0; break;
      case 'lastnpct':  aVal = a.v2_last_n_pct || 0; bVal = b.v2_last_n_pct || 0; break;
      case 'lastndiff': aVal = a.v2_last_n_diff || 0; bVal = b.v2_last_n_diff || 0; break;
      case 'samedate':     aVal = a.v2_same_date_count || 0; bVal = b.v2_same_date_count || 0; break;
      case 'samedatepct':  aVal = a.v2_same_date_pct || 0; bVal = b.v2_same_date_pct || 0; break;
      case 'samedatediff': aVal = a.v2_same_date_diff || 0; bVal = b.v2_same_date_diff || 0; break;
      case 'cymain':       aVal = a.v2_cy_main_count || 0; bVal = b.v2_cy_main_count || 0; break;
      case 'cymainpct':    aVal = a.v2_cy_main_pct || 0; bVal = b.v2_cy_main_pct || 0; break;
      case 'cymaindiff':   aVal = a.v2_cy_main_diff || 0; bVal = b.v2_cy_main_diff || 0; break;
      case 'sigma': {
        const aSum = (a.v2_diff_pct || 0) + (a.v2_may_diff_pre_2026 || 0) + (a.v2_cm_cy_diff || 0)
                   + (a.v2_day_diff || 0) + (a.v2_last_n_diff || 0) + (a.v2_same_date_diff || 0) + (a.v2_cy_main_diff || 0);
        const bSum = (b.v2_diff_pct || 0) + (b.v2_may_diff_pre_2026 || 0) + (b.v2_cm_cy_diff || 0)
                   + (b.v2_day_diff || 0) + (b.v2_last_n_diff || 0) + (b.v2_same_date_diff || 0) + (b.v2_cy_main_diff || 0);
        aVal = aSum; bVal = bSum; break;
      }
      default:        aVal = a.number; bVal = b.number;
    }
    return sortConfig.dir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const SortIcon = ({ k }) => sortConfig.key !== k ? null :
    (sortConfig.dir === 'desc' ? <CaretDown size={11} /> : <CaretUp size={11} />);

  const hCls = "px-2 py-2 text-xs uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200 text-center whitespace-nowrap font-bold border border-slate-300";
  const cCls = "px-2 py-1.5 text-center border border-slate-200 font-mono";

  // Group headers (rendered above the data header row)
  const ordSuffix = (n) => {
    if (n == null) return '';
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}TH`;
    switch (n % 10) {
      case 1: return `${n}ST`;
      case 2: return `${n}ND`;
      case 3: return `${n}RD`;
      default: return `${n}TH`;
    }
  };
  const dayLabel = ordSuffix(nextDrawDay);
  const groupHeaders = [
    { label: 'Identity', cls: 'bg-slate-200 text-slate-800', span: 1 },
    { label: `Pre-2026 (${drawsPre2026} draws)`, cls: 'bg-blue-200 text-blue-900', span: 3 },
    { label: `2026 (${cyDrawsCount} draws)`, cls: 'bg-emerald-200 text-emerald-900', span: 4 },
    { label: `${monthLabel} Till 2025 (${monthDrawsPre2026} draws)`, cls: 'bg-purple-200 text-purple-900', span: 4 },
    { label: `${monthLabel} 2026 (${cyCurrentMonthDraws} draws)`, cls: 'bg-amber-200 text-amber-900', span: 4 },
    { label: `Draw Date (${dayLabel}) till 2026 (${dayTotalDraws} draws)`, cls: 'bg-rose-200 text-rose-900', span: 4 },
    { label: `Last ${lastNDraws} draws × 7 balls (${lastNSlots} slots)`, cls: 'bg-teal-200 text-teal-900', span: 4 },
    { label: `Same Date (${dayLabel} ${nextDrawMonthLabel}) × 7 balls (${sameDateDraws} draws, ${sameDateSlots} slots)`, cls: 'bg-indigo-200 text-indigo-900', span: 4 },
    { label: `In 2026 all numbers Ball 1-5 (${cyMainSlots} slots)`, cls: 'bg-pink-200 text-pink-900', span: 4 },
    { label: 'Σ Total Score', cls: 'bg-yellow-300 text-yellow-900', span: 1 },
  ];

  return (
    <div data-testid={`w65-table-wrap-${ballKey}`}>
      {/* Top mirror scrollbar (synchronized with the table below) */}
      <SyncedTopScrollbar tableRef={tableScrollRef} ballKey={ballKey} minWidth={3200} />
      <div ref={tableScrollRef} className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid={`w65-table-${ballKey}`}>
      <table className="w-full text-sm" style={{ minWidth: '3200px' }}>
        <thead>
          <tr>
            {groupHeaders.map((g, i) => (
              <th
                key={i}
                colSpan={g.span}
                className={`${g.cls} text-[11px] px-2 py-1.5 text-center border border-slate-300 uppercase tracking-tight font-extrabold`}
              >
                {g.label}
              </th>
            ))}
          </tr>
          <tr className="bg-slate-100">
            <th className={`${hCls} bg-slate-200`} onClick={() => onSort('number')} title={`${ballLabel} — number (1..50)`}>
              {ballLabel} <SortIcon k="number" />
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('times25')} title="Total appearances at this ball position up to end of 2025 (pre-2026)">
              Times till 2025 <SortIcon k="times25" />
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('pct25')} title={`Rounded percentage = count ÷ ${drawsPre2026} × 100`}>
              {drawsPre2026} <SortIcon k="pct25" />
            </th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('pct25')} title="Precise pre-2026 percentage (2 decimals)">
              R <SortIcon k="pct25" />
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('cy')} title="Appearances in 2026 at this ball position">
              2026 <SortIcon k="cy" />
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('pct26')} title={`Rounded percentage = 2026 hits ÷ ${cyDrawsCount} × 100`}>
              {cyDrawsCount} <SortIcon k="pct26" />
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('diff')} title="Rounded difference: 2026 % − pre-2026 %">
              % <SortIcon k="diff" />
            </th>
            <th className={`${hCls} bg-emerald-50`} onClick={() => onSort('diff')} title="R1 = difference (2026 % − pre-2026 %) rounded to integer">
              R1 <SortIcon k="diff" />
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('may')} title={`Pre-2026 count of this number at this ball position in ${monthLabel} draws`}>
              {monthLabel} Till 2025 <SortIcon k="may" />
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('maypct')} title={`Rounded percentage = count ÷ ${monthDrawsPre2026} × 100`}>
              {monthDrawsPre2026} <SortIcon k="maypct" />
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('maydiff')} title={`Rounded difference: ${monthLabel} % − pre-2026 %`}>
              % <SortIcon k="maydiff" />
            </th>
            <th className={`${hCls} bg-purple-50`} onClick={() => onSort('maydiff')} title={`R2 = difference (${monthLabel} % − pre-2026 %) rounded to integer`}>
              R2 <SortIcon k="maydiff" />
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('cm')} title={`Count of times this number appeared at this ball position in ${monthLabel} ${new Date().getFullYear()} draws`}>
              Already came in {monthLabel} {new Date().getFullYear()} IN {ballLabel} <SortIcon k="cm" />
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('cmpct')} title={`Rounded percentage = ${monthLabel} ${new Date().getFullYear()} count ÷ ${cyCurrentMonthDraws} × 100`}>
              {cyCurrentMonthDraws} <SortIcon k="cmpct" />
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('cmdiff')} title={`Rounded difference: ${monthLabel} ${new Date().getFullYear()} % − pre-2026 %`}>
              % <SortIcon k="cmdiff" />
            </th>
            <th className={`${hCls} bg-amber-50`} onClick={() => onSort('cmdiff')} title={`R3 = difference (${monthLabel} ${new Date().getFullYear()} % − pre-2026 %) shown to 1 decimal`}>
              R3 <SortIcon k="cmdiff" />
            </th>
            <th className={`${hCls} bg-rose-50`} onClick={() => onSort('day')} title={`Count of times this number appeared at this ball position across ALL draws (any year, any month) that occurred on day ${dayLabel}`}>
              Draw Date ({dayLabel}) till 2026 in {ballLabel} <SortIcon k="day" />
            </th>
            <th className={`${hCls} bg-rose-50`} onClick={() => onSort('daypct')} title={`Rounded percentage = count ÷ ${dayTotalDraws} × 100`}>
              {dayTotalDraws} <SortIcon k="daypct" />
            </th>
            <th className={`${hCls} bg-rose-50`} onClick={() => onSort('daydiff')} title={`Difference: day-${dayLabel} % − pre-2026 % (2 decimals with %)`}>
              % <SortIcon k="daydiff" />
            </th>
            <th className={`${hCls} bg-rose-50`} onClick={() => onSort('daydiff')} title={`R4 = difference (day-${dayLabel} % − pre-2026 %) to 2 decimal places`}>
              R4 <SortIcon k="daydiff" />
            </th>
            <th className={`${hCls} bg-teal-50`} onClick={() => onSort('lastn')} title={`Count of times this number appeared in ANY of the 7 positions across the last ${lastNDraws} draws`}>
              Last {lastNDraws} draws × 7 balls <SortIcon k="lastn" />
            </th>
            <th className={`${hCls} bg-teal-50`} onClick={() => onSort('lastnpct')} title={`Rounded percentage = count ÷ ${lastNSlots} × 100`}>
              {lastNSlots} <SortIcon k="lastnpct" />
            </th>
            <th className={`${hCls} bg-teal-50`} onClick={() => onSort('lastndiff')} title={`Difference: last-${lastNDraws}-draws % − pre-2026 %`}>
              % <SortIcon k="lastndiff" />
            </th>
            <th className={`${hCls} bg-teal-50`} onClick={() => onSort('lastndiff')} title={`R5 = difference (last-${lastNDraws}-draws % − pre-2026 %) rounded to integer`}>
              R5 <SortIcon k="lastndiff" />
            </th>
            <th className={`${hCls} bg-indigo-50`} onClick={() => onSort('samedate')} title={`Count of times this number appeared in ANY of the 7 positions across all historical draws that occurred on ${dayLabel} ${nextDrawMonthLabel} (any year). ${sameDateDraws} matching draws so far.`}>
              Same Date ({dayLabel} {nextDrawMonthLabel}) × 7 balls <SortIcon k="samedate" />
            </th>
            <th className={`${hCls} bg-indigo-50`} onClick={() => onSort('samedatepct')} title={`Rounded percentage = count ÷ ${sameDateSlots} × 100`}>
              {sameDateSlots} <SortIcon k="samedatepct" />
            </th>
            <th className={`${hCls} bg-indigo-50`} onClick={() => onSort('samedatediff')} title={`Difference: same-date % − pre-2026 %`}>
              % <SortIcon k="samedatediff" />
            </th>
            <th className={`${hCls} bg-indigo-50`} onClick={() => onSort('samedatediff')} title={`R6 = difference (same-date % − pre-2026 %) rounded to integer`}>
              R6 <SortIcon k="samedatediff" />
            </th>
            <th className={`${hCls} bg-pink-50`} onClick={() => onSort('cymain')} title={`Count of times this number appeared in ANY of the 5 main ball positions (Ball 1-5, excluding lucky stars) in 2026 draws`}>
              In 2026 all numbers Ball 1-5 <SortIcon k="cymain" />
            </th>
            <th className={`${hCls} bg-pink-50`} onClick={() => onSort('cymainpct')} title={`Rounded percentage = count ÷ ${cyMainSlots} × 100`}>
              {cyMainSlots} <SortIcon k="cymainpct" />
            </th>
            <th className={`${hCls} bg-pink-50`} onClick={() => onSort('cymaindiff')} title={`Difference: 2026-main % − pre-2026 %`}>
              % <SortIcon k="cymaindiff" />
            </th>
            <th className={`${hCls} bg-pink-50`} onClick={() => onSort('cymaindiff')} title={`R7 = difference (2026-main % − pre-2026 %) rounded to integer`}>
              R7 <SortIcon k="cymaindiff" />
            </th>
            <th className={`${hCls} bg-yellow-200 text-yellow-900`} onClick={() => onSort('sigma')} title="Σ = R1 + R2 + R3 + R4 + R5 + R6 + R7 (sum of all R-scores)">
              Σ <SortIcon k="sigma" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const count25 = row.v2_times_till_2025 ?? 0;
            const pct25 = row.v2_pct_pre_2026 ?? 0;
            const cy = row.v2_cy_ball ?? 0;
            const pct26 = row.v2_pct_2026 ?? 0;
            const diff = row.v2_diff_pct ?? 0;
            const mayCount = row.v2_may_pre_2026_count ?? 0;
            const mayPct = row.v2_may_pct_pre_2026 ?? 0;
            const mayDiff = row.v2_may_diff_pre_2026 ?? 0;
            const cmCount = row.v2_cm_cy_count ?? 0;
            const cmPct = row.v2_cm_cy_pct ?? 0;
            const cmDiff = row.v2_cm_cy_diff ?? 0;
            const dayCount = row.v2_day_count ?? 0;
            const dayPct = row.v2_day_pct ?? 0;
            const dayDiff = row.v2_day_diff ?? 0;
            const lastNCount = row.v2_last_n_count ?? 0;
            const lastNPct = row.v2_last_n_pct ?? 0;
            const lastNDiff = row.v2_last_n_diff ?? 0;
            const sameDateCount = row.v2_same_date_count ?? 0;
            const sameDatePct = row.v2_same_date_pct ?? 0;
            const sameDateDiff = row.v2_same_date_diff ?? 0;
            const cyMainCount = row.v2_cy_main_count ?? 0;
            const cyMainPct = row.v2_cy_main_pct ?? 0;
            const cyMainDiff = row.v2_cy_main_diff ?? 0;
            const rCls = pct25 >= 9 ? 'bg-amber-100 text-amber-900 font-bold'
                       : pct25 >= 7 ? 'text-slate-800 font-semibold'
                       : pct25 >= 5 ? 'text-slate-700'
                       : pct25 >= 3 ? 'text-slate-600'
                       : 'text-slate-400';
            return (
              <tr
                key={row.number}
                className="hover:bg-slate-50 transition-colors"
                data-testid={`w65-row-${ballKey}-${row.number}`}
              >
                {/* A */}
                <td className={`${cCls} font-black text-slate-900 bg-slate-50`} data-testid={`w65-num-${ballKey}-${row.number}`}>
                  {row.number}
                </td>
                {/* B */}
                <td className={`${cCls} text-blue-900 font-bold`} data-testid={`w65-count-${ballKey}-${row.number}`}>
                  {count25}
                </td>
                {/* C */}
                <td className={`${cCls} text-emerald-900 font-bold`} data-testid={`w65-pctround-${ballKey}-${row.number}`}>
                  {Math.round(pct25)}%
                </td>
                {/* D */}
                <td className={`${cCls} ${rCls}`} data-testid={`w65-pctprecise-${ballKey}-${row.number}`}>
                  {pct25.toFixed(2)}
                </td>
                {/* E */}
                <td className={`${cCls} font-bold ${cy === 0 ? 'text-slate-300' : 'text-emerald-700'}`} data-testid={`w65-cy-${ballKey}-${row.number}`}>
                  {cy || ''}
                </td>
                {/* F */}
                <td className={`${cCls} text-emerald-900`} data-testid={`w65-pct26-${ballKey}-${row.number}`}>
                  {Math.round(pct26)}%
                </td>
                {/* G */}
                <td className={`${cCls} ${deltaCls(diff)}`} data-testid={`w65-diff-${ballKey}-${row.number}`}>
                  {diff > 0 ? '+' : ''}{Math.round(diff)}%
                </td>
                {/* H */}
                <td className={`${cCls} font-bold ${deltaCls(diff)}`} data-testid={`w65-r1-${ballKey}-${row.number}`}>
                  {diff > 0 ? '+' : ''}{Math.round(diff)}
                </td>
                {/* I */}
                <td className={`${cCls} text-purple-900 font-bold`} data-testid={`w65-may-${ballKey}-${row.number}`}>
                  {mayCount || ''}
                </td>
                {/* J */}
                <td className={`${cCls} text-purple-900`} data-testid={`w65-maypct-${ballKey}-${row.number}`}>
                  {Math.round(mayPct)}%
                </td>
                {/* K */}
                <td className={`${cCls} ${deltaCls(mayDiff)}`} data-testid={`w65-maydiff-${ballKey}-${row.number}`}>
                  {mayDiff > 0 ? '+' : ''}{Math.round(mayDiff)}%
                </td>
                {/* L */}
                <td className={`${cCls} font-bold ${deltaCls(mayDiff)}`} data-testid={`w65-r2-${ballKey}-${row.number}`}>
                  {mayDiff > 0 ? '+' : ''}{Math.round(mayDiff)}
                </td>
                {/* M */}
                <td className={`${cCls} font-bold ${cmCount === 0 ? 'text-slate-300' : 'text-amber-700'}`} data-testid={`w65-cm-${ballKey}-${row.number}`}>
                  {cmCount || ''}
                </td>
                {/* N */}
                <td className={`${cCls} text-amber-900`} data-testid={`w65-cmpct-${ballKey}-${row.number}`}>
                  {Math.round(cmPct)}%
                </td>
                {/* O */}
                <td className={`${cCls} ${deltaCls(cmDiff)}`} data-testid={`w65-cmdiff-${ballKey}-${row.number}`}>
                  {cmDiff > 0 ? '+' : ''}{Math.round(cmDiff)}%
                </td>
                {/* P */}
                <td className={`${cCls} font-bold ${deltaCls(cmDiff)}`} data-testid={`w65-r3-${ballKey}-${row.number}`}>
                  {cmDiff > 0 ? '+' : ''}{cmDiff.toFixed(1)}
                </td>
                {/* Q */}
                <td className={`${cCls} font-bold ${dayCount === 0 ? 'text-slate-300' : 'text-rose-700'}`} data-testid={`w65-day-${ballKey}-${row.number}`}>
                  {dayCount || ''}
                </td>
                {/* R */}
                <td className={`${cCls} text-rose-900`} data-testid={`w65-daypct-${ballKey}-${row.number}`}>
                  {Math.round(dayPct)}%
                </td>
                {/* S */}
                <td className={`${cCls} ${deltaCls(dayDiff)}`} data-testid={`w65-daydiff-${ballKey}-${row.number}`}>
                  {dayDiff > 0 ? '+' : ''}{dayDiff.toFixed(2)}%
                </td>
                {/* T */}
                <td className={`${cCls} font-bold ${deltaCls(dayDiff)}`} data-testid={`w65-r4-${ballKey}-${row.number}`}>
                  {dayDiff > 0 ? '+' : ''}{dayDiff.toFixed(2)}
                </td>
                {/* Y */}
                <td className={`${cCls} font-bold ${lastNCount === 0 ? 'text-slate-300' : 'text-teal-700'}`} data-testid={`w65-lastn-${ballKey}-${row.number}`}>
                  {lastNCount || ''}
                </td>
                {/* Z */}
                <td className={`${cCls} text-teal-900`} data-testid={`w65-lastnpct-${ballKey}-${row.number}`}>
                  {Math.round(lastNPct)}%
                </td>
                {/* AA */}
                <td className={`${cCls} ${deltaCls(lastNDiff)}`} data-testid={`w65-lastndiff-${ballKey}-${row.number}`}>
                  {lastNDiff > 0 ? '+' : ''}{Math.round(lastNDiff)}%
                </td>
                {/* BB */}
                <td className={`${cCls} font-bold ${deltaCls(lastNDiff)}`} data-testid={`w65-r5-${ballKey}-${row.number}`}>
                  {lastNDiff > 0 ? '+' : ''}{Math.round(lastNDiff)}
                </td>
                {/* CC */}
                <td className={`${cCls} font-bold ${sameDateCount === 0 ? 'text-slate-300' : 'text-indigo-700'}`} data-testid={`w65-samedate-${ballKey}-${row.number}`}>
                  {sameDateCount || ''}
                </td>
                {/* DD */}
                <td className={`${cCls} text-indigo-900`} data-testid={`w65-samedatepct-${ballKey}-${row.number}`}>
                  {Math.round(sameDatePct)}%
                </td>
                {/* EE */}
                <td className={`${cCls} ${deltaCls(sameDateDiff)}`} data-testid={`w65-samedatediff-${ballKey}-${row.number}`}>
                  {sameDateDiff > 0 ? '+' : ''}{Math.round(sameDateDiff)}%
                </td>
                {/* FF */}
                <td className={`${cCls} font-bold ${deltaCls(sameDateDiff)}`} data-testid={`w65-r6-${ballKey}-${row.number}`}>
                  {sameDateDiff > 0 ? '+' : ''}{Math.round(sameDateDiff)}
                </td>
                {/* GG */}
                <td className={`${cCls} font-bold ${cyMainCount === 0 ? 'text-slate-300' : 'text-pink-700'}`} data-testid={`w65-cymain-${ballKey}-${row.number}`}>
                  {cyMainCount || ''}
                </td>
                {/* HH */}
                <td className={`${cCls} text-pink-900`} data-testid={`w65-cymainpct-${ballKey}-${row.number}`}>
                  {Math.round(cyMainPct)}%
                </td>
                {/* II */}
                <td className={`${cCls} ${deltaCls(cyMainDiff)}`} data-testid={`w65-cymaindiff-${ballKey}-${row.number}`}>
                  {cyMainDiff > 0 ? '+' : ''}{Math.round(cyMainDiff)}%
                </td>
                {/* JJ — R7 */}
                <td className={`${cCls} font-bold ${deltaCls(cyMainDiff)}`} data-testid={`w65-r7-${ballKey}-${row.number}`}>
                  {cyMainDiff > 0 ? '+' : ''}{Math.round(cyMainDiff)}
                </td>
                {/* KK — Σ = sum of all R deltas */}
                {(() => {
                  const sigma = diff + mayDiff + cmDiff + dayDiff + lastNDiff + sameDateDiff + cyMainDiff;
                  const cls = sigma >= 15 ? 'bg-emerald-400 text-emerald-950 font-black ring-2 ring-emerald-600'
                            : sigma >= 8  ? 'bg-emerald-200 text-emerald-900 font-bold'
                            : sigma >= 3  ? 'bg-emerald-100 text-emerald-800 font-bold'
                            : sigma >= -3 ? 'text-slate-700 font-bold'
                            : sigma >= -8 ? 'bg-rose-100 text-rose-800 font-bold'
                            : sigma >= -15 ? 'bg-rose-200 text-rose-900 font-bold'
                            : 'bg-rose-400 text-rose-950 font-black ring-2 ring-rose-600';
                  return (
                    <td className={`${cCls} ${cls}`} data-testid={`w65-sigma-${ballKey}-${row.number}`} title={`Σ = ${diff.toFixed(2)} + ${mayDiff.toFixed(2)} + ${cmDiff.toFixed(2)} + ${dayDiff.toFixed(2)} + ${lastNDiff.toFixed(2)} + ${sameDateDiff.toFixed(2)} + ${cyMainDiff.toFixed(2)} = ${sigma.toFixed(2)}`}>
                      {sigma > 0 ? '+' : ''}{Math.round(sigma)}
                    </td>
                  );
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};


// ---------- Master Σ Consolidated Table ----------
// One row per number (1..50). Columns: # | B1 Σ | B2 Σ | B3 Σ | B4 Σ | B5 Σ | Grand Σ
const BALL_KEYS_ORDER = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
const MasterSigmaTable = ({ data, sortConfig, onSort }) => {
  if (!data?.balls) return null;
  // Build per-number row: { number, b1, b2, b3, b4, b5, grand }
  const rows = [];
  for (let n = 1; n <= 50; n++) {
    const cells = BALL_KEYS_ORDER.map((bk) => {
      const ballRows = data.balls?.[bk]?.numbers || [];
      const row = ballRows.find((r) => r.number === n);
      return row ? computeRowSigma(row) : 0;
    });
    const grand = cells.reduce((s, v) => s + v, 0);
    rows.push({ number: n, b1: cells[0], b2: cells[1], b3: cells[2], b4: cells[3], b5: cells[4], grand });
  }
  const k = sortConfig.key;
  const sorted = [...rows].sort((a, b) => {
    const av = a[k] ?? 0;
    const bv = b[k] ?? 0;
    return sortConfig.dir === 'desc' ? bv - av : av - bv;
  });
  const SortIcon = ({ kk }) => sortConfig.key !== kk ? null :
    (sortConfig.dir === 'desc' ? <CaretDown size={11} /> : <CaretUp size={11} />);
  const hCls = "px-3 py-2 text-xs uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200 text-center whitespace-nowrap font-bold border border-slate-300";
  const cCls = "px-3 py-1.5 text-center border border-slate-200 font-mono";
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="w65-master-sigma-table">
      <table className="w-full text-sm" style={{ minWidth: '720px' }}>
        <thead>
          <tr>
            <th colSpan={1} className="bg-slate-200 text-slate-800 text-[11px] px-2 py-1.5 text-center border border-slate-300 uppercase tracking-tight font-extrabold">Identity</th>
            <th colSpan={5} className="bg-blue-200 text-blue-900 text-[11px] px-2 py-1.5 text-center border border-slate-300 uppercase tracking-tight font-extrabold">Per-Ball Σ Score</th>
            <th colSpan={1} className="bg-yellow-300 text-yellow-900 text-[11px] px-2 py-1.5 text-center border border-slate-300 uppercase tracking-tight font-extrabold">Grand Σ</th>
          </tr>
          <tr className="bg-slate-100">
            <th className={`${hCls} bg-slate-200`} onClick={() => onSort('number')}>#&nbsp;<SortIcon kk="number" /></th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('b1')} title="Ball 1 Σ (R1..R7 sum)">Ball 1 Σ <SortIcon kk="b1" /></th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('b2')} title="Ball 2 Σ">Ball 2 Σ <SortIcon kk="b2" /></th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('b3')} title="Ball 3 Σ">Ball 3 Σ <SortIcon kk="b3" /></th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('b4')} title="Ball 4 Σ">Ball 4 Σ <SortIcon kk="b4" /></th>
            <th className={`${hCls} bg-blue-50`} onClick={() => onSort('b5')} title="Ball 5 Σ">Ball 5 Σ <SortIcon kk="b5" /></th>
            <th className={`${hCls} bg-yellow-200 text-yellow-900`} onClick={() => onSort('grand')} title="Grand Σ = Ball 1 Σ + Ball 2 Σ + Ball 3 Σ + Ball 4 Σ + Ball 5 Σ">Grand Σ <SortIcon kk="grand" /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.number} className="hover:bg-slate-50 transition-colors" data-testid={`w65-master-row-${row.number}`}>
              <td className={`${cCls} font-black text-slate-900 bg-slate-50`} data-testid={`w65-master-num-${row.number}`}>{row.number}</td>
              {['b1','b2','b3','b4','b5'].map((bk, i) => (
                <td key={bk} className={`${cCls} ${sigmaCls(row[bk])}`} data-testid={`w65-master-${bk}-${row.number}`} title={`Ball ${i+1} Σ = ${row[bk].toFixed(2)}`}>
                  {row[bk] > 0 ? '+' : ''}{Math.round(row[bk])}
                </td>
              ))}
              <td className={`${cCls} ${sigmaCls(row.grand / 5 * 1.5)} text-base`} data-testid={`w65-master-grand-${row.number}`} title={`Grand Σ = ${row.b1.toFixed(1)} + ${row.b2.toFixed(1)} + ${row.b3.toFixed(1)} + ${row.b4.toFixed(1)} + ${row.b5.toFixed(1)} = ${row.grand.toFixed(2)}`}>
                {row.grand > 0 ? '+' : ''}{Math.round(row.grand)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------- R-Parameter Legend (collapsible) ----------
const RLegend = () => {
  const [open, setOpen] = useState(false);
  const items = [
    { code: 'R',  defn: 'Pre-2026 precise %', col: 'bg-blue-100 text-blue-900' },
    { code: 'R1', defn: '2026 % − pre-2026 % (per ball position)', col: 'bg-emerald-100 text-emerald-900' },
    { code: 'R2', defn: `${'May'} Till 2025 % − pre-2026 %`, col: 'bg-purple-100 text-purple-900' },
    { code: 'R3', defn: 'Current month + current year % − pre-2026 % (1 decimal)', col: 'bg-amber-100 text-amber-900' },
    { code: 'R4', defn: 'Same day-of-month (across all years) % − pre-2026 % (2 decimals)', col: 'bg-rose-100 text-rose-900' },
    { code: 'R5', defn: 'Last 6 draws × 7 balls % − pre-2026 %', col: 'bg-teal-100 text-teal-900' },
    { code: 'R6', defn: 'Same Date (day + month) × 7 balls % − pre-2026 %', col: 'bg-indigo-100 text-indigo-900' },
    { code: 'R7', defn: '2026 all numbers × 5 main balls % − pre-2026 %', col: 'bg-pink-100 text-pink-900' },
    { code: 'Σ',  defn: 'R1 + R2 + R3 + R4 + R5 + R6 + R7 (precise sum, displayed as integer)', col: 'bg-yellow-200 text-yellow-900' },
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-4" data-testid="w65-r-legend">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid="w65-r-legend-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>R-Parameter Legend</span>
          <span className="text-xs text-slate-500">— what each column means</span>
        </div>
        {open ? <CaretUp size={18} /> : <CaretDown size={18} />}
      </button>
      {open && (
        <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="w65-r-legend-body">
          {items.map((it) => (
            <div key={it.code} className="flex items-start gap-3">
              <span className={`shrink-0 inline-flex items-center justify-center w-12 h-8 rounded font-extrabold text-sm ${it.col}`}>{it.code}</span>
              <p className="text-xs text-slate-700 leading-snug pt-1">{it.defn}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- page ----------
export default function Window65Method2() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeBall, setActiveBall] = useState('ball_1');
  const [sortConfig, setSortConfig] = useState({ key: 'number', dir: 'asc' });
  const [masterSortConfig, setMasterSortConfig] = useState({ key: 'grand', dir: 'desc' });

  const fetchData = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/w65-method2-frequency`);
      setData(res.data);
    } catch (e) {
      if (!silent) toast.error('Failed to fetch W65 data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSort = (key) => setSortConfig(prev => ({
    key,
    dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
  }));
  const handleMasterSort = (key) => setMasterSortConfig(prev => ({
    key,
    dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
  }));

  const drawsPre2026 = data?.draws_pre_2026 ?? 0;
  const totalDraws = data?.total_draws ?? 0;
  const cyDrawsCount = data?.cy_draws_count ?? (totalDraws - drawsPre2026);
  const monthLabel = data?.current_month_name ?? '';
  const monthDrawsPre2026 = data?.total_pre_cy_month_draws ?? 0;
  const cyCurrentMonthDraws = data?.cy_current_month_draws ?? 0;
  const nextDrawDay = data?.next_draw_day_of_month ?? null;
  const dayTotalDraws = data?.day_total_draws ?? 0;
  const lastNDraws = data?.last_n_draws ?? 0;
  const lastNSlots = data?.last_n_slots ?? 0;
  const sameDateDraws = data?.same_date_total_draws ?? 0;
  const sameDateSlots = data?.same_date_slots ?? 0;
  const nextDrawMonthNum = data?.next_draw_month ?? null;
  const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const nextDrawMonthLabel = nextDrawMonthNum ? MONTH_ABBR[nextDrawMonthNum] : '';
  const cyMainSlots = data?.cy_main_slots ?? 0;
  const ordSuffix = (n) => {
    if (n == null) return '';
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}TH`;
    switch (n % 10) {
      case 1: return `${n}ST`;
      case 2: return `${n}ND`;
      case 3: return `${n}RD`;
      default: return `${n}TH`;
    }
  };
  const dayLabel = ordSuffix(nextDrawDay);

  const handleExport = () => {
    if (!data?.balls) { toast.error('No data to export'); return; }
    const HEADERS = [
      'Ball', '#',
      'Times till 2025', `${drawsPre2026}`, 'R',
      '2026', `${cyDrawsCount}`, '%', 'R1',
      `${monthLabel} Till 2025`, `${monthDrawsPre2026}`, '%', 'R2',
      `Already came in ${monthLabel} ${new Date().getFullYear()}`, `${cyCurrentMonthDraws}`, '%', 'R3',
      `Draw Date (${dayLabel}) till 2026`, `${dayTotalDraws}`, '%', 'R4',
      `Last ${lastNDraws} draws × 7 balls`, `${lastNSlots}`, '%', 'R5',
      `Same Date (${dayLabel} ${nextDrawMonthLabel}) × 7 balls`, `${sameDateSlots}`, '%', 'R6',
      `In 2026 all numbers Ball 1-5`, `${cyMainSlots}`, '%', 'R7',
      'Σ (R1..R7)',
    ];
    const rows = [HEADERS];
    Object.entries(BALL_LABELS).forEach(([bk, label]) => {
      const numbers = data.balls?.[bk]?.numbers || [];
      numbers.forEach(row => {
        const count25 = row.v2_times_till_2025 ?? 0;
        const pct25 = row.v2_pct_pre_2026 ?? 0;
        const cy = row.v2_cy_ball ?? 0;
        const pct26 = row.v2_pct_2026 ?? 0;
        const diff = row.v2_diff_pct ?? 0;
        const mayCount = row.v2_may_pre_2026_count ?? 0;
        const mayPct = row.v2_may_pct_pre_2026 ?? 0;
        const mayDiff = row.v2_may_diff_pre_2026 ?? 0;
        const cmCount = row.v2_cm_cy_count ?? 0;
        const cmPct = row.v2_cm_cy_pct ?? 0;
        const cmDiff = row.v2_cm_cy_diff ?? 0;
        const dayCount = row.v2_day_count ?? 0;
        const dayPct = row.v2_day_pct ?? 0;
        const dayDiff = row.v2_day_diff ?? 0;
        const lastNCount = row.v2_last_n_count ?? 0;
        const lastNPct = row.v2_last_n_pct ?? 0;
        const lastNDiff = row.v2_last_n_diff ?? 0;
        const sameDateCount = row.v2_same_date_count ?? 0;
        const sameDatePct = row.v2_same_date_pct ?? 0;
        const sameDateDiff = row.v2_same_date_diff ?? 0;
        const cyMainCount = row.v2_cy_main_count ?? 0;
        const cyMainPct = row.v2_cy_main_pct ?? 0;
        const cyMainDiff = row.v2_cy_main_diff ?? 0;
        const sigma = diff + mayDiff + cmDiff + dayDiff + lastNDiff + sameDateDiff + cyMainDiff;
        rows.push([
          label, row.number,
          count25, `${Math.round(pct25)}%`, pct25.toFixed(2),
          cy, `${Math.round(pct26)}%`, `${Math.round(diff)}%`, Math.round(diff),
          mayCount, `${Math.round(mayPct)}%`, `${Math.round(mayDiff)}%`, Math.round(mayDiff),
          cmCount, `${Math.round(cmPct)}%`, `${Math.round(cmDiff)}%`, cmDiff.toFixed(1),
          dayCount, `${Math.round(dayPct)}%`, `${dayDiff.toFixed(2)}%`, dayDiff.toFixed(2),
          lastNCount, `${Math.round(lastNPct)}%`, `${Math.round(lastNDiff)}%`, Math.round(lastNDiff),
          sameDateCount, `${Math.round(sameDatePct)}%`, `${Math.round(sameDateDiff)}%`, Math.round(sameDateDiff),
          cyMainCount, `${Math.round(cyMainPct)}%`, `${Math.round(cyMainDiff)}%`, Math.round(cyMainDiff),
          Math.round(sigma),
        ]);
      });
    });
    // ---- Master Σ consolidated section ----
    rows.push([]);  // blank separator row
    rows.push(['MASTER Σ — Consolidated per-number Σ across all 5 ball positions']);
    rows.push(['#', 'Ball 1 Σ', 'Ball 2 Σ', 'Ball 3 Σ', 'Ball 4 Σ', 'Ball 5 Σ', 'Grand Σ']);
    for (let n = 1; n <= 50; n++) {
      const perBall = BALL_KEYS_ORDER.map((bk) => {
        const r = (data.balls?.[bk]?.numbers || []).find((x) => x.number === n);
        return r ? computeRowSigma(r) : 0;
      });
      const grand = perBall.reduce((s, v) => s + v, 0);
      rows.push([n, ...perBall.map((v) => Math.round(v)), Math.round(grand)]);
    }
    const csv = rows.map(r =>
      r.map(c => {
        const s = c == null ? '' : String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `W65_Method2_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('W65 Method 2 exported');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-500 hover:text-slate-700"
                data-testid="back-btn"
              >
                <ArrowLeft size={18} />
              </Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
                <Star size={24} weight="fill" className="text-amber-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  Window 65
                </h1>
                <p className="text-xs text-slate-500">Method 2 — Pre-2026 / 2026 / {monthLabel}-Till-2025 frequency per ball position</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExport}
                disabled={loading || !data}
                className="bg-[#002FA7] hover:bg-[#001F70] text-white flex items-center gap-2"
                data-testid="export-btn"
              >
                <DownloadSimple size={18} /> Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <WindowNarrative windowId="W65" {...(NARRATIVES.W65 || {})} className="mb-5" />

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <CircleNotch size={32} className="animate-spin text-[#002FA7]" />
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-10 gap-3 mb-6">
              <div className="bg-white border border-slate-200 rounded p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total Draws</p>
                <p className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-total-draws">
                  {totalDraws}
                </p>
              </div>
              <div className="bg-white border border-blue-200 rounded p-3 bg-blue-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600">Pre-2026 Draws</p>
                <p className="text-xl font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-pre-2026-draws">
                  {drawsPre2026}
                </p>
              </div>
              <div className="bg-white border border-emerald-200 rounded p-3 bg-emerald-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">2026 Draws</p>
                <p className="text-xl font-bold text-emerald-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-cy-draws">
                  {cyDrawsCount}
                </p>
              </div>
              <div className="bg-white border border-purple-200 rounded p-3 bg-purple-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-purple-700">{monthLabel} Till 2025</p>
                <p className="text-xl font-bold text-purple-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-month-pre-2026-draws">
                  {monthDrawsPre2026}
                </p>
              </div>
              <div className="bg-white border border-amber-200 rounded p-3 bg-amber-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">{monthLabel} {new Date().getFullYear()}</p>
                <p className="text-xl font-bold text-amber-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-cm-cy-draws">
                  {cyCurrentMonthDraws}
                </p>
              </div>
              <div className="bg-white border border-rose-200 rounded p-3 bg-rose-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Day {dayLabel}</p>
                <p className="text-xl font-bold text-rose-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-day-draws">
                  {dayTotalDraws}
                </p>
              </div>
              <div className="bg-white border border-teal-200 rounded p-3 bg-teal-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-teal-700">Last {lastNDraws} × 7</p>
                <p className="text-xl font-bold text-teal-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-lastn-slots">
                  {lastNSlots}
                </p>
              </div>
              <div className="bg-white border border-indigo-200 rounded p-3 bg-indigo-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">{dayLabel} {nextDrawMonthLabel}</p>
                <p className="text-xl font-bold text-indigo-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-samedate-draws">
                  {sameDateDraws}
                </p>
              </div>
              <div className="bg-white border border-pink-200 rounded p-3 bg-pink-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-pink-700">2026 × 5 Main</p>
                <p className="text-xl font-bold text-pink-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }} data-testid="stat-cymain-slots">
                  {cyMainSlots}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded p-3 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Method</p>
                <p className="text-lg font-bold text-slate-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  M2 v0.9
                </p>
              </div>
            </div>

            <RLegend />

            <Tabs value={activeBall} onValueChange={setActiveBall} className="space-y-4">
              <TabsList className="bg-white border border-slate-200 p-1" data-testid="w65-tabs">
                {Object.entries(BALL_LABELS).map(([bk, label]) => (
                  <TabsTrigger
                    key={bk}
                    value={bk}
                    className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold"
                    data-testid={`tab-${bk}`}
                  >
                    {label}
                  </TabsTrigger>
                ))}
                <TabsTrigger
                  value="master"
                  className="data-[state=active]:bg-yellow-500 data-[state=active]:text-yellow-950 font-bold"
                  data-testid="tab-master"
                >
                  Master Σ
                </TabsTrigger>
              </TabsList>
              {Object.entries(BALL_LABELS).map(([bk, label]) => {
                const numbers = data.balls?.[bk]?.numbers || [];
                return (
                  <TabsContent key={bk} value={bk}>
                    <M2BallTable
                      ballKey={bk}
                      ballLabel={label}
                      numbersData={numbers}
                      drawsPre2026={drawsPre2026}
                      cyDrawsCount={cyDrawsCount}
                      monthLabel={monthLabel}
                      monthDrawsPre2026={monthDrawsPre2026}
                      cyCurrentMonthDraws={cyCurrentMonthDraws}
                      nextDrawDay={nextDrawDay}
                      dayTotalDraws={dayTotalDraws}
                      lastNDraws={lastNDraws}
                      lastNSlots={lastNSlots}
                      sameDateDraws={sameDateDraws}
                      sameDateSlots={sameDateSlots}
                      nextDrawMonthLabel={nextDrawMonthLabel}
                      cyMainSlots={cyMainSlots}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    />
                  </TabsContent>
                );
              })}
              <TabsContent value="master">
                <MasterSigmaTable data={data} sortConfig={masterSortConfig} onSort={handleMasterSort} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-20 text-slate-500" data-testid="empty-state">
            No data available.
          </div>
        )}
      </main>
    </div>
  );
}
